import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

 

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        // Verificar se o token ainda é válido (atualiza dados do usuário, incluindo permissões)
        verifyToken();
      } catch (error) {
        console.error('Erro ao parsear dados do usuário:', error);
        logout();
      }
    }
    setLoading(false);
  }, []);

  const verifyToken = async () => {
    try {
      const response = await api.get('/auth/verify');
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    } catch (error) {
      console.error('Token inválido:', error);
      logout();
    }
  };

  const login = async (emailOrToken, passwordOrUserData) => {
    try {
      // Se o primeiro parâmetro é um token JWT (login direto)
      if (typeof emailOrToken === 'string' && emailOrToken.includes('.') && passwordOrUserData && typeof passwordOrUserData === 'object') {
        const token = emailOrToken;
        const userData = passwordOrUserData;
        
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        
        return { success: true };
      }
      
      // Login tradicional com email e senha
      const email = emailOrToken;
      const password = passwordOrUserData;
      
      console.log('=== DEBUG LOGIN ===');
      console.log('Email:', email);
      console.log('Password:', password ? 'Fornecida' : 'Não fornecida');
      
      const response = await api.post('/auth/login', {
        email,
        password
      });

      console.log('Resposta do login:', response.data);
      
      const { token, user: userData } = response.data;
      
      console.log('Token recebido:', token ? 'Sim' : 'Não');
      console.log('Dados do usuário:', userData);
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      try {
        const verify = await api.get('/auth/verify');
        if (verify?.data?.user) {
          setUser(verify.data.user);
          localStorage.setItem('user', JSON.stringify(verify.data.user));
        }
      } catch (e) {
        console.error('Falha ao carregar permissões após login:', e?.response?.data || e.message);
        toast.warning('Falha ao carregar permissões. Tentando novamente mais tarde.');
      }
      
      toast.success('Login realizado com sucesso!');
      return { success: true };
    } catch (error) {
      console.log('=== ERRO NO LOGIN ===');
      console.log('Status:', error.response?.status);
      console.log('Dados do erro:', error.response?.data);
      console.log('Erro completo:', error);
      
      const errorData = error.response?.data;
      const message = errorData?.message || 'Erro ao fazer login';
      
      // Verificar se é erro de email não verificado
      if (error.response?.status === 403 && errorData?.requiresEmailVerification) {
        toast.warning(message);
        return { 
          success: false, 
          message,
          requiresEmailVerification: true,
          email: errorData.email
        };
      }
      
      toast.error(message);
      return { success: false, message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      
      const { token, user: newUser } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(newUser));
      setUser(newUser);
      
      toast.success('Cadastro realizado com sucesso!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao fazer cadastro';
      const errors = error.response?.data?.errors || [];
      
      if (errors.length > 0) {
        errors.forEach(err => toast.error(err.msg));
      } else {
        toast.error(message);
      }
      
      return { success: false, message, errors };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.info('Logout realizado com sucesso!');
  };

  const updateUser = (updatedUser) => {
    console.log('=== DEBUG updateUser FUNCTION ===');
    console.log('Current user before update:', user);
    console.log('Updated user received:', updatedUser);
    console.log('Updated user address:', updatedUser?.address);
    
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    
    // Verificar se foi salvo corretamente
    const savedUser = localStorage.getItem('user');
    console.log('User saved to localStorage:', savedUser);
    console.log('Parsed saved user:', JSON.parse(savedUser));
  };

  const hasRole = useCallback((requiredRoles) => {
    if (!user || !requiredRoles) return false;
    if (Array.isArray(requiredRoles)) {
      return requiredRoles.includes(user.role);
    }
    return user.role === requiredRoles;
  }, [user]);

  const isAdmin = useCallback(() => {
    return user?.role === 'administracao';
  }, [user]);

  const isPastor = useCallback(() => {
    return user?.role === 'pastor';
  }, [user]);

  const isTreasurer = useCallback(() => {
    return user?.role === 'tesoureiro';
  }, [user]);

  const isSecretary = useCallback(() => {
    return user?.role === 'secretario';
  }, [user]);

  const canAccessFinancial = useCallback(() => {
    return hasRole(['tesoureiro', 'pastor', 'administracao']);
  }, [hasRole]);

  const canAccessSecretariat = useCallback(() => {
    return hasRole(['secretario', 'pastor', 'administracao']);
  }, [hasRole]);

  const canAccessAdministration = useCallback(() => {
    return hasRole(['administracao']);
  }, [hasRole]);

  const isLiderCelula = useCallback(() => {
    return user?.permissions?.celulaLider === true;
  }, [user]);

  const canAccessCelulas = useCallback(() => {
    return isLiderCelula() || hasRole(['secretario', 'pastor', 'administracao']);
  }, [isLiderCelula, hasRole]);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    hasRole,
    isAdmin,
    isPastor,
    isTreasurer,
    isSecretary,
    canAccessFinancial,
    canAccessSecretariat,
    canAccessAdministration,
    isLiderCelula,
    canAccessCelulas
  }), [user, loading, login, register, logout, updateUser, hasRole, isAdmin, isPastor, isTreasurer, isSecretary, canAccessFinancial, canAccessSecretariat, canAccessAdministration, isLiderCelula, canAccessCelulas]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
